import BaseListStore from 'stores/BaseListStore';
import TargetGroupStore, { ITargetGroup } from 'stores/targetGroup/TargetGroupStore';
import { get, post } from 'stores/_services/dataService';
import { listSort } from 'stores/_services/utilService';
import { action, observable, makeObservable } from 'mobx';
import { SortDirection } from 'constants/SortDirection';

export enum ScheduleOptions {
    DEFAULT = 'default',
    NONE = 'none',
    CUSTOM = 'custom',
    CONTINUOUS = 'continuous'
}

export interface ICreateTargetArgs {
    name: string;
}

export default class TargetGroupListStore extends BaseListStore<TargetGroupStore> {
    archiveIds = [];

    constructor() {
        super({
            itemStore: TargetGroupStore
        });

        makeObservable(this, {
            archiveIds: observable,
            setSort: action,
            deleteTarget: action,
            enumerate: action,
            createTarget: action
        });
    }

    get filteredItems(): TargetGroupStore[] {
        return this.items.filter((item) => {
            const filter = (this.filter.search || '').toLowerCase();
            const targetGroup = (item as any).targetGroup;
            return targetGroup && targetGroup.name.toLowerCase().includes(filter);
        }) as TargetGroupStore[];
    }

    get archivedItems(): TargetGroupStore[] {
        return this.archiveIds.map((id) => this.data.get(id)) as TargetGroupStore[];
    }

    async getTargetGroups(): Promise<void> {
        const options = {
            filter: {
                order: 'name ASC'
            }
        };

        const { data, count } = await get('targets', options);

        this._updateData(data);
        this._updateIds(data);
        this.count = count;
    }

    async getTargetGroupArchive(): Promise<void> {
        const options = {
            filter: {
                order: `name ${SortDirection.ASC}`
            }
        };

        const { data } = await get('targetsArchive', options);

        this._updateData(data);
        this._updateIds(data, 0, this.archiveIds);
    }

    async getTargetGroup(targetGroupId: string | null | undefined): Promise<void> {
        const options = { filter: {} };

        const { data } = await get(`targets/${targetGroupId}`, options);

        this._updateData(data);
    }

    async setSort({ sortBy = 'name', sortDirection = SortDirection.ASC }): Promise<void> {
        this.sortBy = sortBy;
        this.sortDirection = sortDirection;
        const sorted = listSort(this.items, { sortBy, sortDirection, caseInsensitive: true });
        this.ids = sorted.map((item) => item.targetGroup.id);
    }

    async deleteTarget(targetGroupId: string): Promise<void> {
        const targetGroup = this.findItem(targetGroupId);

        try {
            await targetGroup?.delete();
            this.clearItem(targetGroupId);
        } catch (e) {
            // do nothing
        }
    }

    async enumerate(targetGroupId: string, data: any): Promise<void> {
        const targetGroup = this.findItem(targetGroupId);

        await targetGroup?.enumerate(data);
    }

    async createTarget({ name }: ICreateTargetArgs): Promise<ITargetGroup | undefined> {
        const res = await post('targets', { name });

        const { data } = res;

        return data;
    }
}
